import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCrmMeta, createClient } from '@/hooks/useCrm';
import { useTemplates } from '@/hooks/useTemplates';
import PersonForm, { emptyPerson } from '@/components/PersonForm';
import ValidatedInput from '@/components/ValidatedInput';
import DateInput from '@/components/DateInput';
import { todayIso } from '@/lib/dateFormat';
import { ArrowLeft, Save, Loader2, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { log } from '@/lib/log';

const DRAFT_KEY = 'ral.newClientDraft';
const CLOUD_DRAFT_FORM = 'new-client';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Which person sections are shown for each company type (from Columns PDF)
const SECTIONS_BY_TYPE = {
  'Private Limited': { md: true, directors: true, mp: false, partners: false, proprietor: false },
  'Public':          { md: true, directors: true, mp: false, partners: false, proprietor: false },
  'OPC':             { md: true, directors: false, mp: false, partners: false, proprietor: false },
  'LLP':             { md: false, directors: false, mp: true, partners: true, proprietor: false },
  'Partnership':     { md: false, directors: false, mp: true, partners: true, proprietor: false },
  'Proprietorship':  { md: false, directors: false, mp: false, partners: false, proprietor: true },
};

const CIN_TYPES = ['Private Limited', 'Public', 'OPC'];

const calculateAge = (dob) => {  if (!dob) return '';
  const b = new Date(dob);
  if (Number.isNaN(b.getTime())) return '';
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
};

const SectionHeader = ({ title, action }) => (
  <div className="flex items-center gap-3 mb-5">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    <div className="flex-1 h-px bg-gray-200" />
    {action}
  </div>
);

const Field = ({ label, children, testid, span }) => (
  <div className={span ? `md:col-span-${span} space-y-1.5` : 'space-y-1.5'} data-testid={testid}>
    <Label className="text-xs font-medium text-gray-600  tracking-wider">{label}</Label>
    {children}
  </div>
);

const NewClient = () => {
  const navigate = useNavigate();
  const meta = useCrmMeta();
  const tpl = useTemplates();
  // Prefer /api/crm/meta (accessible to all auth'd users incl. doc team).
  const indianStates = meta?.indian_states?.length ? meta.indian_states : tpl.indianStates;
  const districtsByState = (meta?.districts_by_state && Object.keys(meta.districts_by_state).length)
    ? meta.districts_by_state
    : tpl.districtsByState;
  const [saving, setSaving] = useState(false);

  const [company, setCompany] = useState({
    company_name: '', short_name: '', address: '',
    type: 'Private Limited', date_of_incorporation: '',
    cin: '', pan: '', gst: '',
    state: 'Kerala', district: '',
    bank: '', bank_address: '', account_number: '', ifsc: '',
    email: '', phone: '', networth: '',
    fixed_asset: '', liquid_asset: '', liability: '',
    area_sqft: '',
    landlord_name: '', landlord_address: '', landlord_dob: '', landlord_age: '',
    landlord_aadhar: '',
    rental_amount: '',
    police_station: '', police_station_address: '',
  });

  const [md, setMd] = useState(emptyPerson());
  const [directors, setDirectors] = useState([emptyPerson()]);
  const [mp, setMp] = useState(emptyPerson());
  const [partners, setPartners] = useState([emptyPerson()]);
  const [proprietor, setProprietor] = useState(emptyPerson());

  const [startDate, setStartDate] = useState(todayIso());
  const [remark, setRemark] = useState('');

  // ----- Draft persistence (server-side only) -----
  // Drafts hold client PII, so they live behind auth on the server — never in
  // localStorage, which is readable by any script on the page.
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftSyncState, setDraftSyncState] = useState('cloud'); // 'cloud' | 'local'

  // On mount: restore the server draft (GET /api/drafts/new-client).
  useEffect(() => {
    let cancelled = false;
    const applyDraft = (d) => {
      if (!d) return false;
      if (d.company) setCompany(d.company);
      if (d.md) setMd(d.md);
      if (Array.isArray(d.directors) && d.directors.length) setDirectors(d.directors);
      if (d.mp) setMp(d.mp);
      if (Array.isArray(d.partners) && d.partners.length) setPartners(d.partners);
      if (d.proprietor) setProprietor(d.proprietor);
      if (d.startDate) setStartDate(d.startDate);
      if (typeof d.remark === 'string') setRemark(d.remark);
      toast.info('Draft restored');
      return true;
    };
    // Purge any draft left in localStorage by older builds.
    try { localStorage.removeItem(DRAFT_KEY); } catch (err) { log.debug('[draft] legacy purge failed', err?.message || err); }
    (async () => {
      try {
        const r = await axios.get(`${API}/drafts/${CLOUD_DRAFT_FORM}`);
        if (!cancelled && r.data?.draft?.data) applyDraft(r.data.draft.data);
      } catch (err) {
        log.debug('[draft] restore failed', err?.message || err);
      }
      if (!cancelled) setDraftLoaded(true);
    })();
    return () => { cancelled = true; };
    // Intentional run-once-on-mount restore. Adding all the setters as deps
    // would re-run the restore every time the form changes, which is the
    // opposite of what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced save to the server.
  useEffect(() => {
    if (!draftLoaded) return undefined;
    const t = setTimeout(async () => {
      const payload = { company, md, directors, mp, partners, proprietor, startDate, remark };
      // Only persist if user has typed something meaningful
      if (!(company.company_name || company.cin || company.short_name || remark)) return;
      try {
        await axios.put(`${API}/drafts/${CLOUD_DRAFT_FORM}`, { data: payload });
        setDraftSavedAt(new Date().toISOString());
        setDraftSyncState('cloud');
      } catch (err) {
        log.debug('[draft] cloud save failed → marking offline', err?.message || err);
        setDraftSavedAt(new Date().toISOString());
        setDraftSyncState('local');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [company, md, directors, mp, partners, proprietor, startDate, remark, draftLoaded]);

  const clearDraft = () => {
    axios.delete(`${API}/drafts/${CLOUD_DRAFT_FORM}`)
      .catch((err) => log.debug('[draft] cloud delete failed (offline?)', err?.message || err));
  };

  const sections = SECTIONS_BY_TYPE[company.type] || SECTIONS_BY_TYPE['Private Limited'];

  const setCo = (k, v) => setCompany((p) => {
    const next = { ...p, [k]: v };
    if (k === 'landlord_dob') next.landlord_age = calculateAge(v);
    if (k === 'state') next.district = '';
    if (k === 'type' && !CIN_TYPES.includes(v)) next.cin = '';
    return next;
  });

  const companyDistricts = useMemo(
    () => (company.state && districtsByState?.[company.state]) || [],
    [company.state, districtsByState]
  );
  const hasCompanyDistrictList = companyDistricts.length > 0;

  const handleSave = async () => {
    if (!company.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (CIN_TYPES.includes(company.type) && !company.cin.trim()) {
      toast.error(`CIN is required for ${company.type}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company,
        start_date: startDate,
        remark,
        managing_director: sections.md ? md : null,
        directors: sections.directors ? directors : [],
        managing_partner: sections.mp ? mp : null,
        partners: sections.partners ? partners : [],
        proprietor: sections.proprietor ? proprietor : null,
      };
      const client = await createClient(payload);
      clearDraft();
      toast.success('Client created');
      navigate(`/clients/${client.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8" data-testid="new-client-page">
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">New Client</h1>
            <p className="text-ink/60 mt-1">Documentation Info</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {draftSavedAt && (
            <span className="text-xs text-emerald-600 flex items-center gap-1.5" data-testid="draft-saved-indicator">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {draftSyncState === 'cloud' ? 'Draft saved to cloud' : 'Draft saved (offline)'}
            </span>
          )}
          <Button data-testid="save-client-btn" onClick={handleSave} disabled={saving} className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* Company Details */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <SectionHeader title="Company Details" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Company Name *" testid="field-company-name">
            <Input data-testid="input-company-name" value={company.company_name} onChange={(e) => setCo('company_name', e.target.value)} />
          </Field>
          <Field label="Short Name" testid="field-short-name">
            <Input data-testid="input-short-name" value={company.short_name} onChange={(e) => setCo('short_name', e.target.value)} />
          </Field>
          <Field label="Type" testid="field-type">
            <Select value={company.type} onValueChange={(v) => setCo('type', v)}>
              <SelectTrigger data-testid="select-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.company_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Address" span={3} testid="field-address">
            <Input data-testid="input-address" value={company.address} onChange={(e) => setCo('address', e.target.value)} />
          </Field>

          <Field label="Date of Incorporation" testid="field-doi">
            <DateInput value={company.date_of_incorporation || ''} onChange={(v) => setCo('date_of_incorporation', v)} testid="input-doi" />
          </Field>
          {CIN_TYPES.includes(company.type) && (
            <ValidatedInput
              label="CIN *"
              patterns={meta?.validation_patterns}
              patternKey="cin"
              value={company.cin}
              onChange={(v) => setCo('cin', v)}
              testid="input-cin"
            />
          )}
          <ValidatedInput
            label="PAN"
            patterns={meta?.validation_patterns}
            patternKey="pan"
            value={company.pan}
            onChange={(v) => setCo('pan', v)}
            testid="input-company-pan"
          />

          <ValidatedInput
            label="GST"
            patterns={meta?.validation_patterns}
            patternKey="gst"
            value={company.gst}
            onChange={(v) => setCo('gst', v)}
            testid="input-gst"
          />
          <Field label="State">
            <Select value={company.state} onValueChange={(v) => setCo('state', v)}>
              <SelectTrigger data-testid="select-state"><SelectValue /></SelectTrigger>
              <SelectContent>{(indianStates || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="District">
            {hasCompanyDistrictList ? (
              <Select value={company.district || ''} onValueChange={(v) => setCo('district', v)}>
                <SelectTrigger data-testid="select-district"><SelectValue placeholder="Select district" /></SelectTrigger>
                <SelectContent>{companyDistricts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input data-testid="select-district" placeholder="Enter district" value={company.district || ''} onChange={(e) => setCo('district', e.target.value)} />
            )}
          </Field>

          <Field label="Email">
            <Input data-testid="input-email" type="email" value={company.email} onChange={(e) => setCo('email', e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input data-testid="input-phone" value={company.phone} onChange={(e) => setCo('phone', e.target.value)} />
          </Field>
          <Field label="Networth">
            <Input data-testid="input-networth" value={company.networth} onChange={(e) => setCo('networth', e.target.value)} />
          </Field>
          <Field label="Fixed Asset">
            <Input data-testid="input-fixed-asset" value={company.fixed_asset} onChange={(e) => setCo('fixed_asset', e.target.value)} />
          </Field>
          <Field label="Liquid Asset">
            <Input data-testid="input-liquid-asset" value={company.liquid_asset} onChange={(e) => setCo('liquid_asset', e.target.value)} />
          </Field>
          <Field label="Liability">
            <Input data-testid="input-liability" value={company.liability} onChange={(e) => setCo('liability', e.target.value)} />
          </Field>
          <Field label="Area (Sq. ft.)">
            <Input data-testid="input-area-sqft" value={company.area_sqft} onChange={(e) => setCo('area_sqft', e.target.value)} placeholder="e.g. 1200" />
          </Field>
        </div>

        <div className="mt-6">
          <div className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Banking</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Bank"><Input data-testid="input-bank" value={company.bank} onChange={(e) => setCo('bank', e.target.value)} /></Field>
            <Field label="Bank Address" span={2}><Input data-testid="input-bank-address" value={company.bank_address} onChange={(e) => setCo('bank_address', e.target.value)} /></Field>
            <Field label="Account Number"><Input data-testid="input-account" value={company.account_number} onChange={(e) => setCo('account_number', e.target.value)} /></Field>
            <ValidatedInput
              label="IFSC"
              patterns={meta?.validation_patterns}
              patternKey="ifsc"
              value={company.ifsc}
              onChange={(v) => setCo('ifsc', v)}
              testid="input-ifsc"
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Landlord</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Landlord Name"><Input data-testid="input-landlord-name" value={company.landlord_name} onChange={(e) => setCo('landlord_name', e.target.value)} /></Field>
            <Field label="Landlord Address" span={2}><Input data-testid="input-landlord-address" value={company.landlord_address} onChange={(e) => setCo('landlord_address', e.target.value)} /></Field>
            <Field label="Landlord Date of Birth"><DateInput value={company.landlord_dob || ''} onChange={(v) => setCo('landlord_dob', v)} testid="input-landlord-dob" /></Field>
            <Field label="Landlord Age (auto)"><Input data-testid="input-landlord-age" value={company.landlord_age} readOnly className="bg-gray-50" /></Field>
            <ValidatedInput
              label="Landlord Aadhar"
              patterns={meta?.validation_patterns}
              patternKey="aadhar"
              value={company.landlord_aadhar}
              onChange={(v) => setCo('landlord_aadhar', v)}
              testid="input-landlord-aadhar"
            />
            <Field label="Rental Amount"><Input data-testid="input-rental" value={company.rental_amount} onChange={(e) => setCo('rental_amount', e.target.value)} /></Field>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Police Station</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Police Station">
              <Input data-testid="input-police-station" value={company.police_station} onChange={(e) => setCo('police_station', e.target.value)} placeholder="Station name" />
            </Field>
            <Field label="Police Station Address" span={2}>
              <Input data-testid="input-police-station-address" value={company.police_station_address} onChange={(e) => setCo('police_station_address', e.target.value)} />
            </Field>
          </div>
        </div>
      </section>

      {/* Managing Director */}
      {sections.md && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="section-md">
          <SectionHeader title="Managing Director" />
          <PersonForm value={md} onChange={setMd} dinLabel="DIN" showYearOfGraduation testidPrefix="md" />
        </section>
      )}

      {/* Directors (repeatable) */}
      {sections.directors && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="section-directors">
          <SectionHeader
            title="Directors"
            action={
              <Button
                data-testid="add-director-btn"
                size="sm"
                variant="outline"
                onClick={() => setDirectors([...directors, emptyPerson()])}
                className="rounded-xl"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Director
              </Button>
            }
          />
          {directors.map((d, i) => (
            <div key={d._key} className="border-t border-gray-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0" data-testid={`director-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-700">Director {i + 1}</div>
                {directors.length > 1 && (
                  <Button
                    data-testid={`remove-director-${i}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => setDirectors(directors.filter((_, idx) => idx !== i))}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <PersonForm
                value={d}
                onChange={(nv) => setDirectors(directors.map((x, idx) => idx === i ? nv : x))}
                dinLabel="DIN"
                testidPrefix={`director-${i}`}
              />
            </div>
          ))}
        </section>
      )}

      {/* Managing Partner */}
      {sections.mp && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="section-mp">
          <SectionHeader title="Managing Partner" />
          <PersonForm value={mp} onChange={setMp} dinLabel="DPIN" showYearOfGraduation testidPrefix="mp" />
        </section>
      )}

      {/* Partners (repeatable) */}
      {sections.partners && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="section-partners">
          <SectionHeader
            title="Partners"
            action={
              <Button
                data-testid="add-partner-btn"
                size="sm"
                variant="outline"
                onClick={() => setPartners([...partners, emptyPerson()])}
                className="rounded-xl"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Partner
              </Button>
            }
          />
          {partners.map((p, i) => (
            <div key={p._key} className="border-t border-gray-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0" data-testid={`partner-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-700">Partner {i + 1}</div>
                {partners.length > 1 && (
                  <Button
                    data-testid={`remove-partner-${i}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => setPartners(partners.filter((_, idx) => idx !== i))}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <PersonForm
                value={p}
                onChange={(nv) => setPartners(partners.map((x, idx) => idx === i ? nv : x))}
                dinLabel="DPIN"
                testidPrefix={`partner-${i}`}
              />
            </div>
          ))}
        </section>
      )}

      {/* Proprietor */}
      {sections.proprietor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="section-proprietor">
          <SectionHeader title="Proprietor" />
          <PersonForm value={proprietor} onChange={setProprietor} showDin={false} testidPrefix="proprietor" />
        </section>
      )}

      {/* Sign-Up Date */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <SectionHeader title="Sign-Up Date" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Sign-Up Date">
            <DateInput value={startDate} onChange={setStartDate} testid="input-start-date" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Remark">
              <Textarea data-testid="input-remark" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} />
            </Field>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Deadline auto-calculated as <strong>30 working days</strong> from sign-up date (excluding Indian public holidays).
        </p>
      </section>
    </div>
  );
};

export default NewClient;